// Fonte: uCPFNegativacao.pas (192 linhas) — Negativar/Desnegativar CPF
// Tabela: CPF_Negativacao (Identity PK)
// Regra: tipo I/E calculado automaticamente pelo ultimo registro (Delphi BtnIncluirClick)
// Se ultimo foi 'I' → proximo e 'E' (desnegativar); se 'E' ou vazio → 'I' (negativar)
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Form, Modal, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  negativarCpfConta,
  listarHistoricoNegativacaoConta,
} from '~/Services/Service';
import { cleanCpfCnpj } from '~/Utils/Helpers';
import moment from 'moment';

// Fonte: uCPFNegativacao.pas, BtnIncluirClick — determina proximo tipo baseado no ultimo registro
var determinarProximoTipo = function(historico) {
  if (!historico || historico.length === 0) return 'I';
  // Historico deve vir ordenado por data desc do backend
  var ultimo = historico[0];
  return ultimo.tp === 'I' ? 'E' : 'I';
};

var formatarCpf = function(cpf) {
  if (!cpf) return '-';
  var s = cleanCpfCnpj(cpf);
  if (s.length === 11) {
    return s.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (s.length === 14) {
    return s.replace(
      /([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})([A-Z0-9]{2})/,
      '$1.$2.$3/$4-$5'
    );
  }
  return String(cpf);
};

var TabNegativarCpf = function(_ref) {
  var ge = _ref.ge;
  var codigo = _ref.codigo;
  var cpf = _ref.cpf;

  var [historico, setHistorico] = useState([]);
  var [loading, setLoading] = useState(false);
  var [showModal, setShowModal] = useState(false);
  var [motivo, setMotivo] = useState('');
  var [salvando, setSalvando] = useState(false);
  var [proximoTipo, setProximoTipo] = useState('I');

  var carregar = useCallback(function() {
    if (!ge || !codigo || !cpf) return;
    setLoading(true);
    listarHistoricoNegativacaoConta(ge, codigo, cpf)
      .then(function(resp) {
        var data = Array.isArray(resp) ? resp : [];
        setHistorico(data);
        setProximoTipo(determinarProximoTipo(data));
      })
      .catch(function() { toast.error('Erro ao carregar historico de negativacao'); })
      .finally(function() { setLoading(false); });
  }, [ge, codigo, cpf]);

  useEffect(function() { carregar(); }, [carregar]);

  var handleAbrir = function() {
    setMotivo('');
    setShowModal(true);
  };

  var handleSalvar = function() {
    if (!motivo) { toast.warning('Informe o motivo'); return; }
    // Fonte: uCPFNegativacao.pas, BtnIncluirClick — confirmacao antes de salvar
    var mensagem = proximoTipo === 'I'
      ? 'Deseja negativar o CPF ' + formatarCpf(cpf) + '?'
      : 'Deseja desnegativar o CPF ' + formatarCpf(cpf) + '?';
    if (!window.confirm(mensagem)) return;

    setSalvando(true);
    negativarCpfConta(ge, codigo, { tipo: proximoTipo, motivo: motivo })
      .then(function() {
        toast.success(proximoTipo === 'I' ? 'CPF negativado!' : 'Negativacao removida!');
        setShowModal(false);
        setMotivo('');
        carregar();
      })
      .catch(function(err) {
        var msg = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Erro ao processar negativacao';
        toast.error(msg);
      })
      .finally(function() { setSalvando(false); });
  };

  var isNegativar = proximoTipo === 'I';
  var botaoLabel = isNegativar ? '+ Negativar' : '+ Desnegativar';
  var botaoVariant = isNegativar ? 'danger' : 'warning';

  return (
    <div className="contas-tab-content">
      {cpf && (
        <p className="small mb-2"><strong>CPF/CNPJ:</strong> {formatarCpf(cpf)}</p>
      )}
      <div className="d-flex justify-content-end mb-2">
        <Button size="sm" variant={botaoVariant} onClick={handleAbrir} disabled={!cpf}>
          {botaoLabel}
        </Button>
      </div>

      <p className="small font-weight-bold">Historico</p>
      {loading ? (
        <div className="text-center py-3"><Spinner animation="border" /></div>
      ) : (
        <Table size="sm" bordered className="contas-sub-grid">
          <thead>
            <tr>
              <th>Data</th>
              <th>Tipo</th>
              <th>Usuario</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {historico.length === 0 ? (
              <tr><td colSpan={4} className="text-center text-muted">Nenhum registro de negativacao</td></tr>
            ) : (
              historico.map(function(item, i) {
                return (
                  <tr key={(item.data || '') + '_' + (item.tp || '') + '_' + i}>
                    <td>{item.data ? moment(item.data).format('DD/MM/YYYY HH:mm') : '-'}</td>
                    <td>
                      <Badge variant={item.tp === 'I' ? 'danger' : 'success'}>
                        {item.tp === 'I' ? 'Inclusão' : 'Exclusão'}
                      </Badge>
                    </td>
                    <td>{item.usuario !== null && item.usuario !== undefined ? item.usuario : '-'}</td>
                    <td>{item.motivo !== null && item.motivo !== undefined ? item.motivo : '-'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={function() { setShowModal(false); }}>
        <Modal.Header closeButton className="contas-modal-header">
          <Modal.Title>{isNegativar ? 'Negativar CPF' : 'Desnegativar CPF'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small mb-2">
            <strong>CPF:</strong> {formatarCpf(cpf)}
          </p>
          <p className="small mb-2">
            <strong>Acao:</strong>{' '}
            <Badge variant={isNegativar ? 'danger' : 'warning'}>
              {isNegativar ? 'Negativar (Inclusao)' : 'Desnegativar (Exclusao)'}
            </Badge>
          </p>
          <Form.Group>
            <Form.Label className="small">Motivo *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={motivo}
              onChange={function(e) { setMotivo(e.target.value); }}
              placeholder="Descreva o motivo..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={function() { setShowModal(false); }}>Cancelar</Button>
          <Button variant={isNegativar ? 'danger' : 'warning'} onClick={handleSalvar} disabled={salvando}>
            {salvando ? <Spinner animation="border" size="sm" /> : (isNegativar ? 'Negativar' : 'Desnegativar')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TabNegativarCpf;
